import React, { useMemo } from 'react';
import {
  Button,
  CondensedLabel,
  Form,
  Gutter,
  IconButton,
  Layout,
  LayoutItem,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalSection,
  Paragraph,
  Sortable,
  SortableList,
  Toggle,
} from '@axiom/ui';

import type { MatchGridColumnConfigItem } from '../../types/match-grid.types';
import { MatchGridColumnConfigUtil } from '../../utils/match-grid-column-config-util';

import { DEFAULT_MATCH_GRID_COLUMNS } from './match-grid-columns.const';
import type { MatchGridColumnConfigForm } from './match-grid-column-config-form-schema';
import { MatchGridColumnConfigFormSchema } from './match-grid-column-config-form-schema';

export type MatchGridColumnConfigProps = {
  columns: MatchGridColumnConfigItem[];
  onClose: () => void;
  onOpen: () => void;
  onSave: (columns: MatchGridColumnConfigItem[]) => void;
  showModal: boolean;
};

export const MatchGridColumnConfig = ({
  columns,
  onClose,
  onOpen,
  onSave,
  showModal,
}: MatchGridColumnConfigProps) => {
  const columnItems = useMemo(
    () =>
      columns.flatMap(item => {
        const id = MatchGridColumnConfigUtil.columnIdOf({ item });

        return id
          ? [{ id, isShown: item[id] === true, label: item.label }]
          : [];
      }),
    [columns]
  );

  // Memoised: Form hardcodes enableReinitialize, so fresh initialValues reset it mid-edit.
  const initialValues = useMemo<MatchGridColumnConfigForm>(
    () => ({
      order: columnItems.map(({ id }) => id),
      shown: Object.fromEntries(
        columnItems.map(({ id, isShown }) => [id, isShown])
      ),
    }),
    [columnItems]
  );

  const sortableOptions = useMemo(
    () =>
      columnItems.map(({ id, label }) => ({
        id,
        display: (
          <Layout position="middle">
            <LayoutItem fluid>
              <CondensedLabel name={`GRID_SETTINGS_COLUMN_${id}`}>
                {label}
              </CondensedLabel>
            </LayoutItem>
            <Toggle name={`shown.${id}`} variation="minimal" />
          </Layout>
        ),
      })),
    [columnItems]
  );

  return (
    <>
      <IconButton
        icon="settings"
        name="GRID_SETTINGS_BUTTON"
        onClick={onOpen}
        pattern="secondary"
        title="Talent Details"
      />
      {showModal ? (
        <Form
          initialValues={initialValues}
          name="CONFIG_FORM"
          onSubmit={(changedData, actions, values) => {
            const itemsById = new Map(
              columns.flatMap(item => {
                const id = MatchGridColumnConfigUtil.columnIdOf({ item });

                return id ? [[id, item] as const] : [];
              })
            );

            const configured = values.order.flatMap(id => {
              const item = itemsById.get(id);

              return item
                ? [{ label: item.label, [id]: values.shown[id] ?? false }]
                : [];
            });

            onSave(configured);
          }}
          schema={MatchGridColumnConfigFormSchema}
        >
          {({ fireSubmit }) => (
            <Modal name="GRID_SETTINGS_MODAL">
              <ModalHeader name="GRID_SETTINGS_MODAL_HEADER" onClose={onClose}>
                Talent Details
              </ModalHeader>
              <ModalSection name="GRID_SETTINGS_MODAL_BODY">
                <Paragraph>
                  Customize your columns by dragging them into the order you
                  want. Use the toggles to show or hide each column.
                </Paragraph>
                <Gutter bottom="24px" />
                <Sortable
                  displayKey="display"
                  name="GRID_SETTINGS_COLUMNS"
                  options={sortableOptions}
                  valueKey="id"
                >
                  <SortableList name="order" />
                </Sortable>
                {/*
                  Absent while there is nothing to undo, so it is offered to the reader who has
                  customised and nobody else. Saves as it resets, rather than leaving them to
                  notice the Save button.
                */}
                {MatchGridColumnConfigUtil.isDefault({
                  columns,
                  defaults: DEFAULT_MATCH_GRID_COLUMNS,
                }) ? null : (
                  <>
                    <Gutter top="8px" />
                    <Layout position="left">
                      <Button
                        name="RESET_COLUMNS"
                        onClick={() => {
                          onSave(DEFAULT_MATCH_GRID_COLUMNS);
                        }}
                        pattern="secondary"
                        variation="minimal"
                      >
                        Reset to default
                      </Button>
                    </Layout>
                  </>
                )}
              </ModalSection>
              <ModalFooter>
                <Button name="CLOSE" onClick={onClose} variation="outline">
                  Close
                </Button>
                <Button name="SAVE" onClick={async () => fireSubmit()}>
                  Save
                </Button>
              </ModalFooter>
            </Modal>
          )}
        </Form>
      ) : null}
    </>
  );
};
